import React, { useMemo } from 'react';

import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@mui/material';

import { Model, SelectionTuple, format, parseSelection, personFirst } from './presentationModel';

type Target = Model['targetRows'][number];
/** On narrow screens the metric name stays pinned while the value columns scroll under it. */
const STICKY_METRIC_CLASS = 'kpi-sticky-metric';
type ValueKey = 'actual' | 'forecast' | 'target' | 'override';
const fixedTwo = new Intl.NumberFormat('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
/** Workbook Targets sheets format Actual and Forecast (and MM / Agent Hub Target) as #,##0.00; others keep the default. */
function valueText(row: Target, id: ValueKey, view: string) {
  const value = row[id];
  const fixed = id === 'actual' || id === 'forecast' || (personFirst(view) && id === 'target');
  return fixed && typeof value === 'number' && Number.isFinite(value)
    ? fixedTwo.format(value)
    : format(value, row.unit);
}
type ColumnKey =
  | 'person'
  | 'region'
  | 'rsmRegion'
  | 'label'
  | 'onTrack'
  | 'actual'
  | 'forecast'
  | 'target'
  | 'override'
  | 'forecastRatio';
type Props = {
  rows: Model['targetRows'];
  /** Rows pinned under the table whatever the sort, e.g. the Agent Hub team total. Not clickable. */
  totalRows?: Model['teamTotalRows'];
  view: string;
  selection: string;
  sort: string;
  direction: 'asc' | 'desc';
  onSort: (key: string) => void;
  onSelect: (tuple: SelectionTuple, multi: boolean) => void;
};

export default function CommercialKpiTargets({
  rows,
  totalRows = [],
  view,
  selection,
  sort,
  direction,
  onSort,
  onSelect,
}: Props) {
  const selected = parseSelection(selection);
  const sorted = useMemo(() => {
    if (sort === 'source') {
      return rows;
    }
    const value = (row: Target) => {
      const field = row[sort as ColumnKey];
      return typeof field === 'number' || typeof field === 'string' ? field : null;
    };
    return [...rows].sort((a, b) => {
      const av = value(a);
      const bv = value(b);
      if (av === null) {
        return bv === null ? 0 : 1;
      }
      if (bv === null) {
        return -1;
      }
      const result = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return direction === 'asc' ? result : -result;
    });
  }, [rows, sort, direction]);
  const dimensions: Array<ColumnKey> = personFirst(view) ? ['person', 'region'] : ['rsmRegion', 'region', 'person'];
  const columns: Array<{ id: ColumnKey; label: string }> = personFirst(view)
    ? [
        { id: 'person', label: 'PCM Name' },
        { id: 'region', label: 'PCM Region' },
      ]
    : [
        { id: 'rsmRegion', label: 'RSM Region' },
        { id: 'region', label: 'PCM Region' },
        { id: 'person', label: 'PCM Name' },
      ];
  columns.push(
    { id: 'label', label: 'Metric Name' },
    { id: 'onTrack', label: 'On Track' },
    { id: 'actual', label: 'Actual' },
    { id: 'forecast', label: 'Forecast' },
    { id: 'target', label: 'Target' },
  );
  if (view === 'PCM' || view === 'Experience') {
    columns.push({ id: 'override', label: 'Override' });
  }
  columns.push({ id: 'forecastRatio', label: 'EOP P%' });
  function dimensionSpan(index: number, id: ColumnKey) {
    if (sort !== 'source' || !dimensions.includes(id)) {
      return 1;
    }
    const prefix = dimensions.slice(0, dimensions.indexOf(id) + 1);
    const same = (other: Target) => prefix.every((field) => other[field] === sorted[index][field]);
    if (index > 0 && same(sorted[index - 1])) {
      return 0;
    }
    let span = 1;
    while (index + span < sorted.length && same(sorted[index + span])) {
      span += 1;
    }
    return span;
  }
  return (
    // Phones get one horizontal scroll and the page's own vertical scroll instead of a nested fixed-height box.
    <TableContainer sx={{ maxHeight: { xs: 'none', md: 480 }, overflow: 'auto', minHeight: 0 }}>
      <Table
        stickyHeader
        size="small"
        sx={{
          minWidth: personFirst(view) ? 700 : 880,
          '& .MuiTableCell-root': { py: 0.75, px: 1, fontSize: 12, lineHeight: 1.45 },
          [`& .${STICKY_METRIC_CLASS}`]: {
            position: { xs: 'sticky', md: 'static' },
            left: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            boxShadow: { xs: 1, md: 'none' },
          },
          [`& .MuiTableCell-head.${STICKY_METRIC_CLASS}`]: { zIndex: 3, bgcolor: '#0F464F' },
          '& .MuiTableCell-head': { whiteSpace: 'nowrap', bgcolor: '#0F464F', color: '#FFFFFF' },
          '& .MuiTableSortLabel-root, & .MuiTableSortLabel-root.Mui-active, & .MuiTableSortLabel-icon': {
            color: '#FFFFFF !important',
          },
        }}
        aria-label={`${view} KPI progress`}
      >
        <TableHead>
          <TableRow>
            {columns.map(({ id, label }) => (
              <TableCell
                key={id}
                className={id === 'label' ? STICKY_METRIC_CLASS : undefined}
                align={['actual', 'forecast', 'target', 'override', 'forecastRatio'].includes(id) ? 'right' : 'left'}
              >
                <TableSortLabel
                  active={sort === id}
                  direction={sort === id ? direction : 'asc'}
                  onClick={() => onSort(id)}
                >
                  {label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((row, index) => {
            const active = selected.some(
              (tuple) =>
                tuple.person === row.person &&
                tuple.region === row.region &&
                (personFirst(view) || tuple.rsmRegion === row.rsmRegion),
            );
            return (
              <TableRow
                key={row.id}
                hover
                selected={active}
                onClick={(event) => onSelect(row.tuple, event.ctrlKey || event.metaKey || event.shiftKey)}
                sx={{ cursor: 'pointer' }}
              >
                {columns.map(({ id }) => {
                  const rowSpan = dimensionSpan(index, id);
                  if (!rowSpan) {
                    return null;
                  }
                  return (
                    <TableCell
                      key={id}
                      rowSpan={rowSpan}
                      className={id === 'label' ? STICKY_METRIC_CLASS : undefined}
                      sx={dimensions.includes(id) ? { verticalAlign: 'top' } : undefined}
                      align={
                        ['actual', 'forecast', 'target', 'override', 'forecastRatio'].includes(id) ? 'right' : 'left'
                      }
                    >
                      {id === 'person' ? (
                        <Button
                          size="small"
                          sx={{ p: 0, minWidth: 0, fontSize: 'inherit', lineHeight: 1.4, textAlign: 'left' }}
                        >
                          {row.person ?? 'Unknown'}
                        </Button>
                      ) : id === 'onTrack' ? (
                        <Tooltip
                          title={
                            row.target === 0
                              ? 'Recorded zero target. Workbook formula returns Yes and zero EOP P%.'
                              : 'Yes when forecast is at least target.'
                          }
                        >
                          <Box component="span">
                            {row.onTrack}
                            {row.target === 0 ? ' · zero target' : ''}
                          </Box>
                        </Tooltip>
                      ) : id === 'forecastRatio' ? (
                        format(row.forecastRatio, 'percent')
                      ) : ['actual', 'forecast', 'target', 'override'].includes(id) ? (
                        valueText(row, id as 'actual' | 'forecast' | 'target' | 'override', view)
                      ) : (
                        (row[id] ?? 'Unknown')
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
          {totalRows.map((row, index) => (
            <TableRow key={row.id}>
              {columns.map(({ id }) => (
                <TableCell
                  key={id}
                  align={['actual', 'forecast', 'target', 'override', 'forecastRatio'].includes(id) ? 'right' : 'left'}
                  sx={{
                    fontWeight: 600,
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? '#0F0C08' : '#F3F1EA'),
                    ...(index === 0 ? { borderTop: 2, borderTopColor: 'divider' } : {}),
                  }}
                >
                  {id === 'forecastRatio'
                    ? format(row.forecastRatio, 'percent')
                    : ['actual', 'forecast', 'target', 'override'].includes(id)
                      ? valueText(row, id as ValueKey, view)
                      : id === 'onTrack'
                        ? row.onTrack
                        : (row[id] ?? '')}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {!sorted.length && (
            <TableRow>
              <TableCell colSpan={columns.length}>
                No source rows match the global filters. Values are Unknown.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
